// TODO - Type 'any' needs to be fixed.
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro-6";
import { TFunction } from "i18next";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { formatDate } from "../../../../utils/helpers";
import { StoreType } from "../../../../utils/types";
import TruncatedText from "../../../full-time-shifts/ui/weekly-shift-view/truncated-text";
import useJobsMatrixData from "../../controller/use-jobs-matrix-data";
import {
	clearAuditLog,
	setIsJobDetailsDialogOpen,
} from "../../store/jobs-matrix-slice";


const getStatusIcon = (statusCode: string) => {
	switch (statusCode) {
		case "QUALIFIED":
			return <CheckOutlinedIcon sx={{ fontSize: 16 }} />;
		case "EXPIRED":
			return <AssignmentLateOutlinedIcon sx={{ fontSize: 16 }} />;
		case "NOT_QUALIFIED":
			return null;
		default:
			return null;
	}
};

const getStatusLabel = (statusCode: string, t: TFunction): string => {
	switch (statusCode) {
		case "QUALIFIED":
			return t("jobs-matrix_qualified");
		case "EXPIRED":
			return t("jobs-matrix_expired");
		case "NOT_QUALIFIED":
			return t("jobs-matrix_job-removed");
		default:
			return "--";
	}
};

const getStatusColor = (statusCode: string) => {
	switch (statusCode) {
		case "QUALIFIED":
			return {
				backgroundColor: "#F0FFF7",
				color: "#388E3C",
			};
		case "EXPIRED":
			return {
				backgroundColor: "#F9DEDC",
				color: "#E44B48",
			};
		case "NOT_QUALIFIED":
			return {
				backgroundColor: "#F5F5F5",
				color: "#666",
			};
		default:
			return {
				backgroundColor: "#F5F5F5",
				color: "#666",
			};
	}
};

const WorkerJobDetailsDialog = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { isJobDetailsDialogOpen } = useJobsMatrixData();

	const selectedWorkerAndJob = useSelector(
		(state: StoreType) => state.jobsMatrix?.selectedWorkerAndJob
	);

	const auditLog = useSelector(
		(state: StoreType) => state.jobsMatrix?.fetchWorkerJobAuditLog.data || []
	);

	const auditLogStatus = useSelector(
		(state: StoreType) => state.jobsMatrix?.fetchWorkerJobAuditLog.status
	);

	const allJobs = useSelector(
		(state: StoreType) => state.jobsMatrix?.filterData?.allJobRoles || []
	);


	const handleClose = () => {
		dispatch(setIsJobDetailsDialogOpen(false));
		dispatch(clearAuditLog());
	};

	const jobDetails = allJobs.find(
		(job: any) => job.id === selectedWorkerAndJob?.job.id
	);

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "statusHistory",
				headerName: t("jobs-matrix_status-history"),
				minWidth: 112,
        maxWidth: 112,
				renderCell: (params) => {
					const statusColors = getStatusColor(params.row.statusCode);
					const statusIcon = getStatusIcon(params.row.statusCode);
					const statusLabel = getStatusLabel(params.row.statusCode, t);

					return (
						<Chip
							icon={statusIcon || undefined}
							label={statusLabel}
							size="small"
							sx={{
								backgroundColor: statusColors.backgroundColor,
								fontWeight: 500,
								"& .MuiChip-icon": {
									color: statusColors.color,
								},
                                padding: "2px",
							}}
						/>
					);
				},
			},
			{
				field: "lastUpdated",
				headerName: t("jobs-matrix_last-updated"),
				flex: 1,
				minWidth: 208,
				renderCell: (params) => (
					<Box>
						<Typography variant='body2'>
							{t("jobs-matrix_updated-by", {
								date: formatDate({
									dateStr: params.row.changeDate,
									formatType: "NUMERIC_DATE",
								}),
								name: params.row.changedByName,
							})}
						</Typography>
					</Box>
				),
			},
		],
		[t]
	);

	const rows = useMemo(
		() =>
			auditLog.map((log, index) => ({
				id: index,
				statusCode: log.statusCode,
				changeDate: log.changeDate,
				changedByName: log.changedByName,
			})),
		[auditLog]
	);

	return (
		<>
			<Dialog
				open={isJobDetailsDialogOpen}
				onClose={handleClose}
				aria-describedby='worker-job-details-dialog'
				sx={{
					"& .MuiDialog-paper": {
						width: "480px",
						maxWidth: "480px",
					},
				}}
			>
				<DialogTitle sx={{ padding: "45px 45px 16px" }}>
					{t("jobs-matrix_worker-job-details")}
				</DialogTitle>
				<DialogContent
					sx={{
						padding: "0px 45px",
					}}
				>
					{auditLogStatus === "pending" ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "178px",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<>
							{/* Worker and Job Names */}
							<Box
								sx={{
									display: "flex",
									gap: 3,
									mb: 1,
									alignItems: "center",
									overflow: "hidden",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: "0 1 auto", maxWidth: "50%" }}>
									<PersonOutlineOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
									<TruncatedText
										text={`${selectedWorkerAndJob?.worker.firstName} ${selectedWorkerAndJob?.worker.lastName}`}
										style={{
											fontFamily: "Roboto",
											fontSize: "14px",
											fontWeight: 400,
											lineHeight: "20px",
											color: "#1C1B1F",
											width: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									/>
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: "0 1 auto", maxWidth: "50%" }}>
									<WorkOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
									<TruncatedText
										text={jobDetails?.name || t("jobs-matrix_job")}
										style={{
											fontFamily: "Roboto",
											fontSize: "14px",
											fontWeight: 400,
											lineHeight: "20px",
											color: "#1C1B1F",
											width: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									/>
								</Box>
							</Box>

							{/* DataGrid */}
							<Box sx={{ width: "100%", height: "150px" }}>
								<DataGridPro
									rows={rows}
									columns={columns}
									hideFooter
									disableColumnMenu
									disableColumnFilter
									disableColumnSelector
                  disableColumnResize
                  isRowSelectable={() => false}
                  rowHeight={32}
									sortingOrder={[]}
									sx={{
										border: "none",
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: "transparent",
											borderBottom: "none",
										},
										"& .MuiDataGrid-columnHeader": {
											paddingLeft: 0,
											paddingRight: 0,
										},
										"& .MuiDataGrid-cell": {
											borderBottom: "none",
											paddingLeft: 0,
											paddingRight: 0,
										},
										"& .MuiDataGrid-row": {
											borderBottom: "none",
										},
										"& .MuiDataGrid-columnHeader:focus": {
											outline: "none",
										},
										"& .MuiDataGrid-cell:focus": {
											outline: "none",
										},
										"& .MuiDataGrid-columnHeader:focus-within": {
											outline: "none",
										},
										"& .MuiDataGrid-cell:focus-within": {
											outline: "none",
										},
										"& .MuiDataGrid-virtualScroller": {
											overflowY: "scroll",
											overflowX: "hidden",
											"&::-webkit-scrollbar-thumb": {
                                                minHeight: "30px",
                                             }
										},
										"& .MuiDataGrid-scrollbar--horizontal": {
											display: "none",
										},
										"& .MuiDataGrid-columnSeparator": {
											display: "none",
										},
										"& .MuiDataGrid-sortIcon": {
											display: "none",
										},
										"& .MuiDataGrid-iconButtonContainer": {
											display: "none",
										},
									}}
									localeText={{
										noRowsLabel: t("jobs-matrix_no-status-history"),
									}}
								/>
							</Box>
						</>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						padding: "33px 45px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							gap: "12px",
						}}
					>
						<Box>
							<Button onClick={handleClose} variant='contained'>
								{t("common_close")}
							</Button>
						</Box>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default WorkerJobDetailsDialog;
